import type { UseFormReturnType } from '@mantine/form'
import { useCallback, useState } from 'react'

import type { PresentationStyle } from '@/services/api/api.types'

interface TemplateSelectionState {
  isTemplatePending: boolean
  pendingTemplate: string | null
  originalValue: string | null
  hasUserInteracted: boolean
}

interface UseTemplateSelectionProps {
  form: UseFormReturnType<any>
  fieldName: string
}

export function useTemplateSelection({ form, fieldName }: UseTemplateSelectionProps) {
  const [state, setState] = useState<TemplateSelectionState>({
    isTemplatePending: false,
    pendingTemplate: null,
    originalValue: null,
    hasUserInteracted: false,
  })

  const resetState = useCallback(() => {
    setState({
      isTemplatePending: false,
      pendingTemplate: null,
      originalValue: null,
      hasUserInteracted: false,
    })
  }, [])

  const selectTemplate = useCallback(
    (template: PresentationStyle) => {
      const currentValue = form.getValues()[fieldName]
      setState({
        isTemplatePending: true,
        pendingTemplate: template.description,
        originalValue: currentValue?.trim() || null,
        hasUserInteracted: true,
      })
    },
    [form, fieldName],
  )

  const acceptTemplate = useCallback(() => {
    if (state.pendingTemplate) {
      form.setFieldValue(fieldName, state.pendingTemplate)
      // Clear any validation errors since the field now has valid content
      form.clearFieldError(fieldName)
      setState(prev => ({
        ...prev,
        isTemplatePending: false,
        pendingTemplate: null,
        originalValue: null,
      }))
    }
  }, [state.pendingTemplate, form, fieldName])

  const rejectTemplate = useCallback(() => {
    if (state.originalValue) {
      form.setFieldValue(fieldName, state.originalValue)
    }

    // Reset hasUserInteracted if we're reverting to an empty state
    const shouldResetInteraction = !state.originalValue?.trim()

    setState(prev => ({
      ...prev,
      isTemplatePending: false,
      pendingTemplate: null,
      originalValue: null,
      hasUserInteracted: shouldResetInteraction ? false : prev.hasUserInteracted,
    }))
  }, [state.originalValue, form, fieldName])

  const markUserInteraction = useCallback(() => {
    setState(prev => ({ ...prev, hasUserInteracted: true }))
  }, [])

  // Get input props for the textarea, handling template pending state
  const getInputProps = useCallback(() => {
    const baseProps = form.getInputProps(fieldName)

    if (state.isTemplatePending) {
      return {
        ...baseProps, // Keep all original props including error handling
        defaultValue: undefined, // Override uncontrolled defaultValue
        value: state.pendingTemplate || '', // Force controlled mode for pending template
        onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          // If user starts typing while template is pending, reject the template
          markUserInteraction()

          // Set the field value directly instead of calling rejectTemplate to avoid resetting hasUserInteracted
          form.setFieldValue(fieldName, e.target.value)
          setState(prev => ({
            ...prev,
            isTemplatePending: false,
            pendingTemplate: null,
            originalValue: null,
            hasUserInteracted: true, // Always mark as interacted when user manually changes content
          }))

          baseProps.onChange?.(e)
        },
      }
    }

    return {
      ...baseProps,
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        markUserInteraction()
        baseProps.onChange?.(e)
      },
    }
  }, [form, fieldName, state.isTemplatePending, state.pendingTemplate, markUserInteraction, rejectTemplate])

  // Check if template cards should be shown
  const shouldShowTemplateCards = useCallback(() => {
    const fieldValue = form.getValues()[fieldName]
    const isFieldEmpty = !fieldValue?.trim() && !state.isTemplatePending
    return isFieldEmpty && !state.hasUserInteracted
  }, [form, fieldName, state.isTemplatePending, state.hasUserInteracted])

  return {
    state,
    resetState,
    selectTemplate,
    acceptTemplate,
    rejectTemplate,
    markUserInteraction,
    getInputProps,
    shouldShowTemplateCards,
  }
}
